import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Check, Clock, ExternalLink, FileText, Upload, XCircle } from "lucide-react";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  RequestActivityList,
  professionalPayoutActivityConfig,
  type ActivityEvent,
} from "@/components/shared/RequestActivityList";
import { openAuthenticatedBlobInNewTab } from "@/utils/openAuthenticatedBlobInNewTab";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatUyMinor } from "@/lib/formatUyMinor";
import { statusBadgeVariant, statusLabelKey } from "@/lib/professionalPayoutStatus";
import {
  accountTypeLabelEs,
  getUruguayBank,
  type UruguayBankAccountType,
} from "@gestionalo/shared-types";
import {
  attachPlatformProfessionalPayoutReceipt,
  fetchPlatformProfessionalPayoutReceiptBlob,
  getPlatformProfessionalPayoutRequest,
  transitionPlatformProfessionalPayout,
  type PlatformProfessionalPayoutDetail,
  type PlatformProfessionalPayoutDetailProfessional,
} from "@/services/professionalEarningsService";

type TransitionTarget = "processing" | "paid" | "cancelled";

const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
const RECEIPT_ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
];
const RECEIPT_ACCEPT_ATTR = RECEIPT_ACCEPTED_MIME_TYPES.join(",");

/** Client-side validation that mirrors the backend whitelist + size cap. */
function validateReceiptFile(file: File): string | null {
  if (!RECEIPT_ACCEPTED_MIME_TYPES.includes(file.type)) {
    return "Formato no soportado. Subí un PDF o imagen (JPG/PNG/WEBP).";
  }
  if (file.size > RECEIPT_MAX_BYTES) {
    return "El archivo supera el límite de 10 MB.";
  }
  return null;
}

/** Pretty bank snapshot labels; any extra keys fall back to humanized camelCase. */
const BANK_FIELD_LABELS: Record<string, string> = {
  holderName: "Titular",
  holderDocumentId: "Documento",
  institutionCode: "Banco",
  accountNumber: "Cuenta",
  accountType: "Tipo de cuenta",
};

function humanizeKey(key: string): string {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

/** Resolve raw bank-snapshot values (institution codes, account type keys) to human-readable text. */
function formatBankFieldValue(key: string, value: string): string {
  if (key === "institutionCode") {
    const bank = getUruguayBank(value);
    return bank ? `${bank.shortName} (${bank.code})` : value;
  }
  if (key === "accountType" && (value === "savings" || value === "checking")) {
    return accountTypeLabelEs(value as UruguayBankAccountType);
  }
  return value;
}

export function PlatformProfessionalPayoutDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(undefined, { keyPrefix: "platformProfessionalPayouts" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pendingTarget, setPendingTarget] = useState<TransitionTarget | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [payReceiptFile, setPayReceiptFile] = useState<File | null>(null);
  const [payReceiptError, setPayReceiptError] = useState<string | null>(null);
  const [attachReceiptOpen, setAttachReceiptOpen] = useState(false);
  const [attachReceiptFile, setAttachReceiptFile] = useState<File | null>(null);
  const [attachReceiptError, setAttachReceiptError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["platform", "professional-payout", id],
    queryFn: () => getPlatformProfessionalPayoutRequest(id!),
    enabled: Boolean(id),
  });

  const resetTransitionDialogs = (): void => {
    setPendingTarget(null);
    setCancelReason("");
    setPayReceiptFile(null);
    setPayReceiptError(null);
  };

  const resetAttachReceiptDialog = (): void => {
    setAttachReceiptOpen(false);
    setAttachReceiptFile(null);
    setAttachReceiptError(null);
  };

  const invalidatePayoutCaches = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["platform", "professional-payout"] });
    void queryClient.invalidateQueries({ queryKey: ["platform", "professional-payouts"] });
    void detailQuery.refetch();
  };

  const transitionMutation = useMutation({
    mutationFn: ({
      target,
      reason,
      receipt,
    }: {
      target: TransitionTarget;
      reason?: string | null;
      receipt?: File;
    }) =>
      transitionPlatformProfessionalPayout(
        id!,
        { status: target, reason: reason ?? null },
        receipt,
      ),
    onSuccess: (_data, variables) => {
      const msgKey =
        variables.target === "processing"
          ? "markProcessingSuccess"
          : variables.target === "paid"
            ? "markPaidSuccess"
            : "cancelPayoutSuccess";
      toast.success(t(msgKey));
      resetTransitionDialogs();
      invalidatePayoutCaches();
    },
    onError: () => {
      toast.error(t("transitionError"));
    },
  });

  const attachReceiptMutation = useMutation({
    mutationFn: ({ receipt }: { receipt: File }) =>
      attachPlatformProfessionalPayoutReceipt(id!, receipt),
    onSuccess: () => {
      toast.success(t("attachReceiptSuccess"));
      resetAttachReceiptDialog();
      invalidatePayoutCaches();
    },
    onError: () => {
      toast.error(t("attachReceiptError"));
    },
  });

  const viewReceiptMutation = useMutation({
    mutationFn: () => openAuthenticatedBlobInNewTab(() => fetchPlatformProfessionalPayoutReceiptBlob(id!)),
    onError: () => {
      toast.error(t("viewReceiptError"));
    },
  });

  if (detailQuery.isLoading || !detailQuery.data) {
    return <LoadingPage />;
  }

  const d = detailQuery.data;

  const confirmCopy = pendingTarget
    ? {
        processing: {
          title: t("markProcessingConfirmTitle"),
          description: t("markProcessingConfirmDescription"),
          confirm: t("markProcessingConfirm"),
          submitting: t("markProcessingSubmitting"),
        },
        paid: {
          title: t("markPaidConfirmTitle"),
          description: t("markPaidConfirmDescription"),
          confirm: t("markPaidConfirm"),
          submitting: t("markPaidSubmitting"),
        },
        cancelled: {
          title: t("cancelConfirmTitle"),
          description: t("cancelConfirmDescription"),
          confirm: t("cancelConfirm"),
          submitting: t("markPaidSubmitting"),
        },
      }[pendingTarget]
    : null;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("detailTitle")}
        description={t("detailSubtitle", { id: d.id.slice(0, 8) })}
        onBack={() => navigate("/professional-payouts")}
      />

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <SummaryCard
            detail={d}
            onTransition={(target) => setPendingTarget(target)}
            onViewReceipt={() => viewReceiptMutation.mutate()}
            onAttachReceipt={() => setAttachReceiptOpen(true)}
            isViewingReceipt={viewReceiptMutation.isPending}
          />
          <ProfessionalCard professional={d.professional} />
          <ItemsCard
            detail={d}
            onOpenRequest={(lineId) =>
              navigate(`/requests/${lineId}`, {
                state: { from: `/professional-payouts/${d.id}` },
              })
            }
          />
        </div>
        <div className="space-y-4">
          <BankCard bankSnapshot={d.bankSnapshot} />
          <TimelineCard detail={d} />
        </div>
      </div>

      <ConfirmationDialog
        open={pendingTarget === "processing"}
        onOpenChange={(next) => {
          if (!next) setPendingTarget(null);
        }}
        title={confirmCopy?.title ?? ""}
        description={confirmCopy?.description ?? ""}
        confirmText={confirmCopy?.confirm ?? ""}
        onConfirm={() =>
          pendingTarget === "processing"
            ? transitionMutation.mutate({ target: "processing" })
            : undefined
        }
        isLoading={transitionMutation.isPending}
        loadingText={confirmCopy?.submitting ?? ""}
      />

      <Dialog
        open={pendingTarget === "paid"}
        onOpenChange={(next) => {
          if (!next && !transitionMutation.isPending) resetTransitionDialogs();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("markPaidConfirmTitle")}</DialogTitle>
            <DialogDescription>{t("markPaidConfirmDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="pay-receipt" className="text-sm font-medium text-foreground">
              {t("receiptFileLabel")}
            </Label>
            <Input
              id="pay-receipt"
              type="file"
              accept={RECEIPT_ACCEPT_ATTR}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                if (!file) {
                  setPayReceiptFile(null);
                  setPayReceiptError(null);
                  return;
                }
                const err = validateReceiptFile(file);
                setPayReceiptError(err);
                setPayReceiptFile(err ? null : file);
              }}
            />
            <p className="text-xs text-muted-foreground">{t("receiptFileHint")}</p>
            {payReceiptError ? (
              <p className="text-xs text-destructive">{payReceiptError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={transitionMutation.isPending}
              onClick={resetTransitionDialogs}
            >
              {t("dialogCancel")}
            </Button>
            <Button
              type="button"
              disabled={!payReceiptFile || transitionMutation.isPending}
              onClick={() => {
                if (!payReceiptFile) return;
                transitionMutation.mutate({ target: "paid", receipt: payReceiptFile });
              }}
            >
              {transitionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("markPaidSubmitting")}
                </>
              ) : (
                t("markPaidConfirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={attachReceiptOpen}
        onOpenChange={(next) => {
          if (!next && !attachReceiptMutation.isPending) resetAttachReceiptDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("attachReceiptTitle")}</DialogTitle>
            <DialogDescription>{t("attachReceiptDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="attach-receipt" className="text-sm font-medium text-foreground">
              {t("receiptFileLabel")}
            </Label>
            <Input
              id="attach-receipt"
              type="file"
              accept={RECEIPT_ACCEPT_ATTR}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                if (!file) {
                  setAttachReceiptFile(null);
                  setAttachReceiptError(null);
                  return;
                }
                const err = validateReceiptFile(file);
                setAttachReceiptError(err);
                setAttachReceiptFile(err ? null : file);
              }}
            />
            <p className="text-xs text-muted-foreground">{t("receiptFileHint")}</p>
            {attachReceiptError ? (
              <p className="text-xs text-destructive">{attachReceiptError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={attachReceiptMutation.isPending}
              onClick={resetAttachReceiptDialog}
            >
              {t("dialogCancel")}
            </Button>
            <Button
              type="button"
              disabled={!attachReceiptFile || attachReceiptMutation.isPending}
              onClick={() => {
                if (!attachReceiptFile) return;
                attachReceiptMutation.mutate({ receipt: attachReceiptFile });
              }}
            >
              {attachReceiptMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("attachReceiptSubmitting")}
                </>
              ) : (
                t("attachReceiptConfirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingTarget === "cancelled"}
        onOpenChange={(next) => {
          if (!next && !transitionMutation.isPending) resetTransitionDialogs();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("cancelConfirmTitle")}</DialogTitle>
            <DialogDescription>{t("cancelConfirmDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason" className="text-sm font-medium text-foreground">
              {t("cancelReasonLabel")}
            </Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t("cancelReasonPlaceholder")}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={transitionMutation.isPending}
              onClick={resetTransitionDialogs}
            >
              {t("dialogCancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={transitionMutation.isPending}
              onClick={() =>
                transitionMutation.mutate({
                  target: "cancelled",
                  reason: cancelReason.trim() || null,
                })
              }
            >
              {transitionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("markPaidSubmitting")}
                </>
              ) : (
                t("cancelConfirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({
  detail,
  onTransition,
  onViewReceipt,
  onAttachReceipt,
  isViewingReceipt,
}: {
  detail: PlatformProfessionalPayoutDetail;
  onTransition: (target: TransitionTarget) => void;
  onViewReceipt: () => void;
  onAttachReceipt: () => void;
  isViewingReceipt: boolean;
}): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformProfessionalPayouts" });
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-foreground">{t("detailSummaryTitle")}</CardTitle>
        <StatusBadge variant={statusBadgeVariant(detail.status)}>
          {t(statusLabelKey(detail.status))}
        </StatusBadge>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          <p className="text-muted-foreground">{t("detailAmount")}</p>
          <p className="text-2xl font-semibold tracking-tight text-foreground">
            {formatUyMinor(detail.totalMinor, detail.currency)}
          </p>
        </div>

        <Separator />

        <div className="flex flex-wrap gap-2">
          {detail.status === "pending" ? (
            <Button type="button" variant="outline" onClick={() => onTransition("processing")}>
              <Clock className="mr-2 h-4 w-4" />
              {t("markProcessing")}
            </Button>
          ) : null}
          {detail.status === "processing" ? (
            <Button type="button" onClick={() => onTransition("paid")}>
              <Check className="mr-2 h-4 w-4" />
              {t("markPaid")}
            </Button>
          ) : null}
          {detail.status === "pending" || detail.status === "processing" ? (
            <Button type="button" variant="outline" onClick={() => onTransition("cancelled")}>
              <XCircle className="mr-2 h-4 w-4" />
              {t("cancelPayout")}
            </Button>
          ) : null}
          {detail.status === "paid" && detail.hasReceipt ? (
            <Button
              type="button"
              variant="outline"
              disabled={isViewingReceipt}
              onClick={onViewReceipt}
            >
              {isViewingReceipt ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              {t("viewReceipt")}
            </Button>
          ) : null}
          {detail.status === "paid" && !detail.hasReceipt ? (
            <Button type="button" onClick={onAttachReceipt}>
              <Upload className="mr-2 h-4 w-4" />
              {t("attachReceipt")}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function ProfessionalCard({
  professional,
}: {
  professional: PlatformProfessionalPayoutDetailProfessional;
}): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformProfessionalPayouts" });
  const roleLabel =
    professional.role === "notary"
      ? t("detailProfessionalRoleNotary")
      : professional.role === "accountant"
        ? t("detailProfessionalRoleAccountant")
        : t("detailProfessionalRoleOther");
  const rows: Array<{ label: string; value: string | null }> = [
    { label: t("detailProfessionalEmail"), value: professional.email },
    { label: t("detailProfessionalPhone"), value: professional.phone },
    {
      label: t("detailProfessionalDocument"),
      value:
        professional.documentNumber ??
        professional.cedula ??
        null,
    },
    { label: t("detailProfessionalMatricula"), value: professional.notaryMatricula },
  ];
  const visibleRows = rows.filter((r) => r.value != null && r.value.trim() !== "");
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-foreground">
          <span>{t("detailProfessionalTitle")}</span>
          <span className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
            {roleLabel}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-lg font-semibold text-foreground">
          {professional.fullName ?? professional.email ?? professional.id}
        </p>
        <dl className="grid gap-2 sm:grid-cols-2">
          {visibleRows.map((r) => (
            <div key={r.label} className="min-w-0">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">{r.label}</dt>
              <dd className="truncate text-sm text-foreground">{r.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function ItemsCard({
  detail,
  onOpenRequest,
}: {
  detail: PlatformProfessionalPayoutDetail;
  onOpenRequest: (requestId: string) => void;
}): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformProfessionalPayouts" });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">{t("linesTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("lineRequest")}</TableHead>
              <TableHead>{t("lineRequester")}</TableHead>
              <TableHead className="text-right">{t("lineAmount")}</TableHead>
              <TableHead className="w-[64px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.lines.map((line) => {
              const requestId = line.notarialRequestId ?? line.accountantRequestId;
              return (
                <TableRow
                  key={line.id}
                  className={requestId ? "cursor-pointer" : undefined}
                  onClick={requestId ? () => onOpenRequest(requestId) : undefined}
                >
                  <TableCell>
                    <p className="font-medium text-foreground">
                      {line.requestLabel ?? t("lineNoLabel")}
                    </p>
                    {requestId ? (
                      <p className="font-mono text-[11px] text-muted-foreground">
                        {requestId.slice(0, 8)}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {line.requesterName ?? "—"}
                  </TableCell>
                  <TableCell className="text-right font-medium text-foreground">
                    {formatUyMinor(line.amountMinor, line.currency)}
                  </TableCell>
                  <TableCell className="text-right">
                    {requestId ? (
                      <ExternalLink className="inline h-4 w-4 text-muted-foreground" />
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function TimelineCard({ detail }: { detail: PlatformProfessionalPayoutDetail }): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformProfessionalPayouts" });
  const events: ActivityEvent[] = [
    { id: "created", type: "created", createdAt: detail.createdAt },
  ];
  if (detail.processingAt) {
    events.push({
      id: "marked_processing",
      type: "marked_processing",
      createdAt: detail.processingAt,
    });
  }
  if (detail.paidAt) {
    events.push({ id: "marked_paid", type: "marked_paid", createdAt: detail.paidAt });
  }
  if (detail.cancelledAt) {
    events.push({
      id: "cancelled",
      type: "cancelled",
      createdAt: detail.cancelledAt,
      message: detail.notes ?? null,
    });
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">{t("detailTimelineTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-72 overflow-y-auto pr-1">
          <RequestActivityList
            events={events}
            config={professionalPayoutActivityConfig()}
            emptyMessage={t("detailTimelineEmpty")}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function BankCard({ bankSnapshot }: { bankSnapshot: Record<string, string> }): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformProfessionalPayouts" });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">{t("detailBankTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-2 text-sm">
          {Object.entries(bankSnapshot).map(([k, v]) => (
            <div key={k} className="grid grid-cols-[120px_1fr] gap-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {BANK_FIELD_LABELS[k] ?? humanizeKey(k)}
              </dt>
              <dd className="truncate font-mono text-xs text-foreground">
                {formatBankFieldValue(k, v)}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
